import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { eventDateRange } from '../lib/events.js';
import { EVENT_SALES_COLOR, EVENT_SALES_LABEL } from '../lib/constants.js';

// ─── Location events calendar (Schedule view) ──────────────────────────────
// 2-month calendar grid + list. Reachable from a "📅 Events" button on
// the Schedule view. Read-only for all roles.
export function LocationEventsCalendar({ locationId, locationName, onClose }) {
  const [events, setEvents] = useState([]);            // events in the visible 2-month grid
  const [allEvents, setAllEvents] = useState([]);      // events in a 12-month horizon (right pane)
  const [loading, setLoading] = useState(true);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [anchorMonth, setAnchorMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // 12-month horizon, fetched once per mount. Also auto-anchors the
  // calendar to the first month with an event so the user lands where
  // their events are instead of staring at an empty May when their
  // first event is in July.
  useEffect(() => {
    if (!locationId) return;
    const start = new Date(today);
    const end = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    const params = new URLSearchParams({
      location_id: String(locationId),
      from: start.toISOString().slice(0, 10),
      to:   end.toISOString().slice(0, 10),
    });
    api('GET', `/api/events?${params.toString()}`)
      .then(r => {
        const list = Array.isArray(r?.events) ? r.events : [];
        setAllEvents(list);
        // Auto-anchor: if no events fall in the default (current) 2-month
        // window but there are events later, jump to the first event's month.
        if (list.length === 0) return;
        const firstYmd = String(list[0].start_date).slice(0, 10);
        const cur = anchorMonth;
        const winEnd = new Date(cur.getFullYear(), cur.getMonth() + 2, 0);
        const firstD = new Date(firstYmd + 'T12:00:00');
        if (firstD > winEnd) {
          setAnchorMonth(new Date(firstD.getFullYear(), firstD.getMonth(), 1));
        }
      })
      .catch(() => setAllEvents([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (!locationId) return;
    setLoading(true);
    const fromD = new Date(anchorMonth);
    fromD.setDate(1);
    const toD = new Date(anchorMonth);
    toD.setMonth(toD.getMonth() + 2); toD.setDate(0); // last day of 2nd month
    const from = fromD.toISOString().slice(0, 10);
    const to   = toD.toISOString().slice(0, 10);
    const params = new URLSearchParams({ location_id: String(locationId), from, to });
    api('GET', `/api/events?${params.toString()}`)
      .then(r => setEvents(Array.isArray(r?.events) ? r.events : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [locationId, anchorMonth]);

  function jumpToEvent(e) {
    const d = new Date(String(e.start_date).slice(0, 10) + 'T12:00:00');
    setAnchorMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  function shiftMonths(n) {
    setAnchorMonth(m => new Date(m.getFullYear(), m.getMonth() + n, 1));
  }

  // Build a Set of date strings that have events for fast cell lookup.
  const eventsByDay = (() => {
    const m = new Map();
    for (const e of events) {
      const s = String(e.start_date).slice(0, 10);
      const en = String(e.end_date || e.start_date).slice(0, 10);
      const cur = new Date(s + 'T12:00:00');
      const last = new Date(en + 'T12:00:00');
      while (cur <= last) {
        const ymd = cur.toISOString().slice(0, 10);
        const arr = m.get(ymd) || []; arr.push(e); m.set(ymd, arr);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  })();

  function MonthGrid({ month }) {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startDow = first.getDay(); // 0 = Sun
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const monthLabel = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return (
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{monthLabel}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
                        fontSize: 10, color: '#888', textAlign: 'center', marginBottom: 2 }}>
          {['S','M','T','W','T','F','S'].map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((d, i) => {
            if (d == null) return <div key={i} />;
            const ymd = `${month.getFullYear()}-${String(month.getMonth()+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
            const dayEvents = eventsByDay.get(ymd) || [];
            const isToday = ymd === today.toISOString().slice(0, 10);
            const has = dayEvents.length > 0;
            const restricted = dayEvents.some(e => e.staffing_restricted);
            return (
              <div key={i}
                style={{ aspectRatio: '1', display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', fontSize: 12,
                          border: isToday ? '2px solid var(--gold)' : '1px solid #eee',
                          borderRadius: 6,
                          background: restricted ? '#fef9ee' : has ? '#e8f5fe' : '#fff',
                          color: '#333', position: 'relative' }}>
                <div style={{ fontWeight: has ? 700 : 400 }}>{d}</div>
                {has && (
                  <div style={{ width: 5, height: 5, borderRadius: '50%',
                                  background: restricted ? '#8e6c33' : '#1565c0',
                                  marginTop: 2 }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const monthA = anchorMonth;
  const monthB = new Date(anchorMonth.getFullYear(), anchorMonth.getMonth() + 1, 1);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,31,29,0.45)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
          onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 16, maxWidth: 920, width: '100%',
                      maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>📅 Events {locationName ? `· ${locationName}` : ''}</div>
            <div style={{ fontSize: 11, color: '#888' }}>2-month calendar with event list</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => shiftMonths(-1)}
              style={{ padding: '4px 10px', border: '1px solid #d0d4d9', borderRadius: 6,
                        background: '#fff', cursor: 'pointer', fontSize: 13 }}>← Prev</button>
            <button onClick={() => setAnchorMonth(new Date(today.getFullYear(), today.getMonth(), 1))}
              style={{ padding: '4px 10px', border: '1px solid #d0d4d9', borderRadius: 6,
                        background: '#fff', cursor: 'pointer', fontSize: 13 }}>Today</button>
            <button onClick={() => shiftMonths(1)}
              style={{ padding: '4px 10px', border: '1px solid #d0d4d9', borderRadius: 6,
                        background: '#fff', cursor: 'pointer', fontSize: 13 }}>Next →</button>
            <button onClick={onClose}
              style={{ padding: '4px 12px', border: '1px solid #d0d4d9', borderRadius: 6,
                        background: '#fafafa', cursor: 'pointer', fontSize: 13 }}>Close</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 16, alignItems: 'start' }}
              className="events-grid-responsive">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <MonthGrid month={monthA} />
            <MonthGrid month={monthB} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#666',
                            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Upcoming events (next 12 months)
            </div>
            {loading && allEvents.length === 0 ? (
              <div style={{ fontSize: 13, color: '#888' }}>Loading…</div>
            ) : allEvents.length === 0 ? (
              <div style={{ fontSize: 13, color: '#888', fontStyle: 'italic' }}>
                No events in the next 12 months.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allEvents.map(e => {
                  const c = EVENT_SALES_COLOR[e.sales_impact] || EVENT_SALES_COLOR.unknown;
                  return (
                    <div key={`${e.id}-${e.start_date}`}
                      style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{e.title}</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                        {eventDateRange(e)}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                        background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
                          {EVENT_SALES_LABEL[e.sales_impact] || e.sales_impact}
                        </span>
                        {e.staffing_restricted && (
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                          background: '#fef9ee', color: '#8e6c33', border: '1px solid #f5e6c8' }}>
                            🔒 Restricted
                          </span>
                        )}
                        {e.is_annual && (
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                          background: '#e8f5fe', color: '#1565c0', border: '1px solid #b3d6f5' }}>
                            🔁 Annual
                          </span>
                        )}
                      </div>
                      {e.notes && <div style={{ fontSize: 12, color: '#444', marginTop: 6 }}>{e.notes}</div>}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                        <button onClick={() => jumpToEvent(e)}
                          style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #d0d4d9',
                                    borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                          Show in calendar
                        </button>
                        {e.url && (
                          <a href={e.url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 11, color: '#1976d2' }}>
                            🔗 Website
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <style>{`
          @media (max-width: 760px) {
            .events-grid-responsive { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </div>
  );
}
